import React, { useState, useEffect, useRef, useMemo } from 'react';
import { X, Check, Calendar, MapPin, FileText, Filter, GraduationCap, ChevronDown, ChevronUp } from 'lucide-react';
import { CalendarEvent, groupEventsByCourse } from '@/lib/icalParser';

interface EventAssignmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  events: CalendarEvent[];
  notebooks: Array<{ id: string; name: string }>;
  onAssign: (assignments: EventAssignment[]) => void;
}

export interface EventAssignment {
  event: CalendarEvent;
  notebookId: string;
  category: 'assignment' | 'discussion' | 'experiential' | 'journal';
  import: boolean;
}

const categories = [
  { value: 'assignment', label: 'Assignments', icon: '📝' },
  { value: 'discussion', label: 'Discussion', icon: '💬' },
  { value: 'experiential', label: 'Experiential', icon: '✨' },
  { value: 'journal', label: 'Journal & Logs', icon: '📋' },
] as const;

/**
 * Try to match a course code to a notebook name (case-insensitive partial match)
 */
function matchNotebookByCourseCode(courseCode: string, notebooks: EventAssignmentModalProps['notebooks']): string | undefined {
  const upper = courseCode.toUpperCase();
  return notebooks.find(nb => nb.name.toUpperCase().includes(upper))?.id;
}

const EventAssignmentModal: React.FC<EventAssignmentModalProps> = ({
  isOpen,
  onClose,
  events,
  notebooks,
  onAssign,
}) => {
  const [assignments, setAssignments] = useState<EventAssignment[]>([]);
  const initializedRef = useRef(false);

  // ── Filters ─────────────────────────────────────────────────────────────
  const [selectedCourses, setSelectedCourses] = useState<Set<string>>(new Set());
  const [semesterStart, setSemesterStart] = useState<string>('');
  const [semesterEnd, setSemesterEnd] = useState<string>('');
  const [showFilters, setShowFilters] = useState(true); // Show filters by default

  /**
   * Compute current semester date range based on today's date
   * Fall: Aug 1 – Dec 31 | Spring: Jan 1 – Apr 30 | Summer: May 1 – Jul 31
   */
  function getCurrentSemesterRange(): { start: string; end: string } {
    const now = new Date();
    const month = now.getMonth();
    const year = now.getFullYear();

    if (month >= 7) {
      // Fall (Aug–Dec)
      return { start: `${year}-08-01`, end: `${year}-12-31` };
    } else if (month >= 4) {
      // Summer (May–Jul)
      return { start: `${year}-05-01`, end: `${year}-07-31` };
    } else {
      // Spring (Jan–Apr)
      return { start: `${year}-01-01`, end: `${year}-04-30` };
    }
  }

  // ── Detect unique course codes from events ──────────────────────────────
  const courseCodes = useMemo(() => {
    const codes = new Set<string>();
    for (const event of events) {
      if (event.courseCode) codes.add(event.courseCode);
    }
    return Array.from(codes).sort();
  }, [events]);

  // Auto-select all courses on mount
  useEffect(() => {
    if (courseCodes.length > 0 && selectedCourses.size === 0) {
      setSelectedCourses(new Set(courseCodes));
    }
  }, [courseCodes]);

  // Initialize assignments only once when modal opens with events
  useEffect(() => {
    if (isOpen && events.length > 0 && !initializedRef.current) {
      // Auto-set semester date range based on current date
      const range = getCurrentSemesterRange();
      setSemesterStart(range.start);
      setSemesterEnd(range.end);

      setAssignments(
        events.map(event => {
          // Auto-match to notebook by course code
          const matchedNotebookId = event.courseCode
            ? matchNotebookByCourseCode(event.courseCode, notebooks)
            : undefined;
          return {
            event,
            notebookId: matchedNotebookId || notebooks[0]?.id || '',
            category: 'assignment' as const,
            import: true,
          };
        })
      );
      initializedRef.current = true;
    }

    // Reset when modal closes
    if (!isOpen) {
      initializedRef.current = false;
      setAssignments([]);
      setSelectedCourses(new Set());
      setSemesterStart('');
      setSemesterEnd('');
    }
  }, [isOpen, events, notebooks]);

  // ── Filtered events based on course code and semester range ─────────────
  const filteredAssignments = useMemo(() => {
    return assignments.filter(a => {
      // Course code filter
      if (selectedCourses.size > 0 && a.event.courseCode) {
        if (!selectedCourses.has(a.event.courseCode)) return false;
      }
      // Semester date range filter
      if (semesterStart) {
        const start = new Date(semesterStart);
        if (a.event.startDate < start) return false;
      }
      if (semesterEnd) {
        const end = new Date(semesterEnd);
        end.setHours(23, 59, 59, 999); // End of day
        if (a.event.startDate > end) return false;
      }
      return true;
    });
  }, [assignments, selectedCourses, semesterStart, semesterEnd]);

  const hiddenCount = assignments.length - filteredAssignments.length;

  // ── Handlers ────────────────────────────────────────────────────────────
  const toggleCourse = (code: string) => {
    setSelectedCourses(prev => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  };

  const handleToggleImport = (index: number) => {
    setAssignments(prev =>
      prev.map((a, i) => (i === index ? { ...a, import: !a.import } : a))
    );
  };

  const handleNotebookChange = (index: number, notebookId: string) => {
    setAssignments(prev =>
      prev.map((a, i) => (i === index ? { ...a, notebookId } : a))
    );
  };

  const handleCategoryChange = (index: number, category: EventAssignment['category']) => {
    setAssignments(prev =>
      prev.map((a, i) => (i === index ? { ...a, category } : a))
    );
  };

  const handleImport = () => {
    const toImport = filteredAssignments.filter(a => a.import);
    onAssign(toImport);
  };

  const handleSelectAll = () => {
    setAssignments(prev => prev.map(a => ({ ...a, import: true })));
  };

  const handleDeselectAll = () => {
    setAssignments(prev => prev.map(a => ({ ...a, import: false })));
  };

  const selectedCount = filteredAssignments.filter(a => a.import).length;
  const allSelected = selectedCount === filteredAssignments.length;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="bg-gradient-to-br from-gray-900 via-purple-900/50 to-gray-900 rounded-2xl border-2 border-amber-400/30 shadow-2xl w-full max-w-4xl max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="bg-gradient-to-r from-amber-600 to-yellow-500 p-4 flex items-center justify-between border-b-2 border-amber-700 rounded-t-xl flex-shrink-0">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Assign Calendar Events</h2>
            <p className="text-sm text-gray-700">
              {events.length} total • {filteredAssignments.length} shown • {selectedCount} selected
              {hiddenCount > 0 && <span className="text-gray-600"> ({hiddenCount} hidden by filters)</span>}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`px-3 py-1 rounded-lg text-sm font-semibold transition-colors flex items-center gap-1 ${
                showFilters ? 'bg-white/30 text-gray-900' : 'bg-white/20 hover:bg-white/30 text-gray-900'
              }`}
            >
              <Filter className="w-4 h-4" />
              Filters
              {showFilters ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            </button>
            <button
              onClick={allSelected ? handleDeselectAll : handleSelectAll}
              className="px-3 py-1 bg-white/20 hover:bg-white/30 rounded-lg text-sm font-semibold text-gray-900 transition-colors"
            >
              {allSelected ? 'Deselect All' : 'Select All'}
            </button>
            <button
              onClick={onClose}
              className="p-1 hover:bg-white/20 rounded-lg transition-colors"
              title="Close"
            >
              <X className="w-6 h-6 text-gray-900" />
            </button>
          </div>
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <div className="p-4 border-b border-gray-700 bg-gray-800/40 space-y-3">
            {/* Course Code Filter */}
            {courseCodes.length > 0 && (
              <div>
                <label className="block text-xs text-amber-300 font-semibold mb-2 flex items-center gap-1">
                  <GraduationCap className="w-3.5 h-3.5" />
                  Filter by Course
                </label>
                <div className="flex flex-wrap gap-2">
                  {courseCodes.map(code => (
                    <button
                      key={code}
                      onClick={() => toggleCourse(code)}
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${
                        selectedCourses.has(code)
                          ? 'bg-amber-500/20 border-amber-400/50 text-amber-200'
                          : 'bg-gray-700/50 border-gray-600 text-gray-400 hover:border-gray-500'
                      }`}
                    >
                      {code}
                    </button>
                  ))}
                  {courseCodes.length === 0 && (
                    <span className="text-xs text-gray-500">No course codes detected in event titles</span>
                  )}
                </div>
              </div>
            )}

            {/* Semester Date Range */}
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <label className="block text-xs text-amber-300 font-semibold mb-1">Semester Start</label>
                <input
                  type="date"
                  value={semesterStart}
                  onChange={e => setSemesterStart(e.target.value)}
                  className="w-full bg-gray-800 border border-gray-600 rounded-lg px-3 py-1.5 text-white text-sm focus:border-amber-400 focus:outline-none"
                />
              </div>
              <div className="flex-1">
                <label className="block text-xs text-amber-300 font-semibold mb-1">Semester End</label>
                <input
                  type="date"
                  value={semesterEnd}
                  onChange={e => setSemesterEnd(e.target.value)}
                  className="w-full bg-gray-800 border border-gray-600 rounded-lg px-3 py-1.5 text-white text-sm focus:border-amber-400 focus:outline-none"
                />
              </div>
              {(semesterStart || semesterEnd) && (
                <button
                  onClick={() => { setSemesterStart(''); setSemesterEnd(''); }}
                  className="px-2 py-1.5 text-xs text-gray-400 hover:text-white self-end"
                >
                  Clear
                </button>
              )}
            </div>
          </div>
        )}

        {/* Event List */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4 min-h-0">
          {filteredAssignments.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <Calendar className="w-16 h-16 mx-auto mb-4 opacity-50" />
              <p>{hiddenCount > 0 ? 'No events match the current filters' : 'No events to display'}</p>
              {hiddenCount > 0 && (
                <button
                  onClick={() => { setSelectedCourses(new Set(courseCodes)); setSemesterStart(''); setSemesterEnd(''); }}
                  className="text-sm text-amber-400 underline mt-2 hover:text-amber-300"
                >
                  Clear all filters
                </button>
              )}
            </div>
          ) : (
            filteredAssignments.map((assignment, index) => {
              // Find the actual index in the full assignments array for handlers
              const realIndex = assignments.indexOf(assignment);
              return (
              <div
                key={assignment.event.id}
                className={`rounded-xl border-2 p-4 transition-all ${
                  assignment.import
                    ? 'border-amber-400/50 bg-amber-400/10'
                    : 'border-gray-700 bg-gray-800/50 opacity-60'
                }`}
              >
                <div className="flex gap-4">
                  {/* Checkbox */}
                  <div className="flex-shrink-0 pt-1">
                    <button
                      onClick={() => handleToggleImport(realIndex)}
                      className={`w-6 h-6 rounded border-2 flex items-center justify-center transition-colors ${
                        assignment.import
                          ? 'bg-amber-500 border-amber-500'
                          : 'bg-gray-700 border-gray-600'
                      }`}
                    >
                      {assignment.import && <Check className="w-4 h-4 text-gray-900" />}
                    </button>
                  </div>

                  {/* Event Details */}
                  <div className="flex-1 space-y-3">
                    <div>
                      <h3 className="text-white font-semibold text-lg">
                        {assignment.event.title}
                      </h3>
                      <div className="flex items-center gap-4 text-sm text-gray-400 mt-1 flex-wrap">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {assignment.event.startDate.toLocaleDateString()}
                          {!assignment.event.allDay && ` ${assignment.event.startDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`}
                        </span>
                        {assignment.event.courseCode && (
                          <span className="px-2 py-0.5 rounded-full bg-purple-500/20 border border-purple-400/30 text-purple-200 text-xs font-semibold">
                            {assignment.event.courseCode}
                          </span>
                        )}
                        {assignment.event.location && (
                          <span className="flex items-center gap-1">
                            <MapPin className="w-4 h-4" />
                            {assignment.event.location}
                          </span>
                        )}
                      </div>
                      {assignment.event.description && assignment.event.description.trim() && !assignment.event.description.startsWith('http') && (
                        <p className="text-gray-400 text-sm mt-2 line-clamp-2">
                          {assignment.event.description.split('\n').filter(line => !line.trim().startsWith('http')).join(' ').trim()}
                        </p>
                      )}
                    </div>

                    {/* Assignment Controls */}
                    {assignment.import && (
                      <div className="grid grid-cols-2 gap-3">
                        {/* Notebook Selection */}
                        <div>
                          <label className="block text-xs text-amber-300 font-semibold mb-1">
                            Add to Notebook
                          </label>
                          <select
                            value={assignment.notebookId}
                            onChange={(e) => handleNotebookChange(realIndex, e.target.value)}
                            className="w-full bg-gray-800 border-2 border-gray-600 rounded-lg px-3 py-2 text-white text-sm focus:border-amber-400 focus:outline-none"
                          >
                            {notebooks.map(notebook => (
                              <option key={notebook.id} value={notebook.id}>
                                {notebook.name}
                              </option>
                            ))}
                          </select>
                          {assignment.event.courseCode && (
                            <p className="text-[10px] text-gray-500 mt-1">
                              {matchNotebookByCourseCode(assignment.event.courseCode, notebooks)
                                ? '✓ Auto-matched to notebook'
                                : 'No matching notebook found — create one with this course code'}
                            </p>
                          )}
                        </div>

                        {/* Category Selection */}
                        <div>
                          <label className="block text-xs text-amber-300 font-semibold mb-1">
                            Category
                          </label>
                          <select
                            value={assignment.category}
                            onChange={(e) => handleCategoryChange(realIndex, e.target.value as EventAssignment['category'])}
                            className="w-full bg-gray-800 border-2 border-gray-600 rounded-lg px-3 py-2 text-white text-sm focus:border-amber-400 focus:outline-none"
                          >
                            {categories.map(cat => (
                              <option key={cat.value} value={cat.value}>
                                {cat.icon} {cat.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )})
          )}
        </div>

        {/* Footer Actions */}
        <div className="p-6 border-t-2 border-gray-700 flex gap-3 flex-shrink-0 bg-gray-900/50">
          <button
            onClick={onClose}
            className="flex-1 bg-gray-700 hover:bg-gray-600 text-white font-semibold py-3 px-6 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleImport}
            disabled={selectedCount === 0}
            className="flex-1 bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-gray-900 font-bold py-3 px-6 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Import {selectedCount} {selectedCount === 1 ? 'Event' : 'Events'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EventAssignmentModal;